import { useEffect, useRef, useState } from 'react';
import TimeSheetApi from '@infrastructure/timeSheet-api';
function pdfSignature(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = () => reject(new Error('The PDF could not be read.'));
        reader.readAsText(blob.slice(0, 5));
    });
}
// A document must still belong to this mounted actor/selection after every await,
// including Blob decoding. The server also rejects cross-account token refresh.
export function useMonthPdf(key, request, checkActor) {
    const [state, setState] = useState({ key: '', busy: false });
    const liveKey = useRef(key);
    liveKey.current = key;
    const mounted = useRef(true);
    const generation = useRef(0);
    const lock = useRef(false);
    const urls = useRef(new Map());
    const release = () => {
        urls.current.forEach((timer, url) => { clearTimeout(timer); URL.revokeObjectURL(url); });
        urls.current.clear();
    };
    useEffect(() => {
        generation.current++;
        lock.current = false;
        release();
        setState({ key, busy: false });
    }, [key]);
    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; generation.current++; release(); };
    }, []);
    const download = async () => {
        if (!key || lock.current || !checkActor())
            return;
        const attempt = generation.current;
        const current = () => mounted.current && attempt === generation.current && key === liveKey.current && checkActor();
        lock.current = true;
        setState({ key, busy: true });
        try {
            const response = await TimeSheetApi.downloadMonthPdf({ ...request });
            if (!current())
                return;
            const blob = response.data;
            const contentType = String(response.headers?.['content-type'] || blob?.type || '').split(';')[0].trim().toLowerCase();
            if (!(blob instanceof Blob) || contentType !== 'application/pdf' || await pdfSignature(blob) !== '%PDF-')
                throw new Error('Invalid PDF response.');
            if (!current())
                return;
            const url = URL.createObjectURL(blob);
            const timer = setTimeout(() => { URL.revokeObjectURL(url); urls.current.delete(url); }, 1000);
            urls.current.set(url, timer);
            let link;
            try {
                link = document.createElement('a');
                link.href = url;
                link.download = `QPortal-timesheet-${request.year}-${String(request.month).padStart(2, '0')}-${request.assignmentId.slice(0, 8)}.pdf`;
                document.body.appendChild(link);
                link.click();
            }
            catch (error) {
                clearTimeout(timer);
                urls.current.delete(url);
                URL.revokeObjectURL(url);
                throw error;
            }
            finally {
                link?.remove();
            }
        }
        catch {
            if (current())
                setState({ key, busy: false, error: 'The PDF could not be downloaded. Your saved hours are unchanged. Please try again.' });
        }
        finally {
            if (attempt === generation.current)
                lock.current = false;
            if (current())
                setState(previous => ({ ...previous, key, busy: false }));
        }
    };
    return { download, busy: state.key === key && state.busy, error: state.key === key ? state.error : undefined };
}
